import type { DesignDocument, DesignNode } from '../design-builder.types';
import { parseDesignDocument } from './design-json.utils';

const PREVIEW_WIDTH = 240;
const PREVIEW_HEIGHT = 135;

/** Produce a safe, dependency-free SVG image for the Templates gallery. */
export function templateThumbnailDataUrl(template: Record<string, unknown>): string {
  const stored = template['thumbnail'];
  if (typeof stored === 'string' && /^(data:image\/|https?:\/\/)/i.test(stored)) return stored;

  const { document } = parseDesignDocument(JSON.stringify(template));
  if (!document) return fallbackThumbnail(String(template['name'] ?? 'Template'));
  return documentThumbnail(document);
}

function documentThumbnail(document: DesignDocument): string {
  const width = Math.max(1, document.canvas.width);
  const height = Math.max(1, document.canvas.height);
  const scale = Math.min(PREVIEW_WIDTH / width, PREVIEW_HEIGHT / height);
  const drawWidth = width * scale;
  const drawHeight = height * scale;
  const offsetX = (PREVIEW_WIDTH - drawWidth) / 2;
  const offsetY = (PREVIEW_HEIGHT - drawHeight) / 2;
  const nodes = document.pages[0]?.nodes ?? [];
  const body = nodes.map((node) => renderNode(node)).join('');
  return svgUrl(`
    <rect width="${PREVIEW_WIDTH}" height="${PREVIEW_HEIGHT}" fill="#eef2f6"/>
    <g transform="translate(${round(offsetX)} ${round(offsetY)}) scale(${scale})">
      <rect width="${width}" height="${height}" fill="${safeColor(document.canvas.background, '#ffffff')}"/>
      ${body}
    </g>`);
}

function renderNode(node: DesignNode): string {
  if (!node.visible) return '';
  const transform = `translate(${round(node.x)} ${round(node.y)}) rotate(${round(node.rotation || 0)} ${round(node.width / 2)} ${round(node.height / 2)})`;
  const fill = safeColor(node.style?.fill?.color, node.type === 'FRAME' ? '#ffffff' : '#d0d5dd');
  const stroke = node.style?.stroke;
  const strokeAttrs = stroke
    ? ` stroke="${safeColor(stroke.color, '#98a2b3')}" stroke-width="${Math.max(0, stroke.width)}"${strokeOpacityAttr(stroke.opacity)}`
    : '';
  const radius = typeof node.style?.radius === 'number' ? Math.max(0, node.style.radius) : 0;
  let shape = '';

  switch (node.type) {
    case 'ELLIPSE':
      shape = `<ellipse cx="${round(node.width / 2)}" cy="${round(node.height / 2)}" rx="${round(node.width / 2)}" ry="${round(node.height / 2)}" fill="${fill}"${strokeAttrs}/>`;
      break;
    case 'LINE':
      shape = `<line x1="0" y1="0" x2="${round(node.width)}" y2="${round(node.height)}" stroke="${safeColor(stroke?.color, '#667085')}" stroke-width="${Math.max(1, stroke?.width ?? 2)}"/>`;
      break;
    case 'TEXT': {
      const text = escapeXml(node.text?.value || node.name);
      const size = Math.max(8, node.text?.fontSize ?? 16);
      shape = `<text x="0" y="${round(size)}" fill="${safeColor(node.text?.color, '#344054')}" font-family="Arial,sans-serif" font-size="${size}" font-weight="${node.text?.fontWeight ?? 400}">${text.slice(0, 80)}</text>`;
      break;
    }
    case 'IMAGE':
      shape = `<rect width="${round(node.width)}" height="${round(node.height)}" rx="${radius}" fill="#e4e7ec"/><path d="M ${round(node.width * .15)} ${round(node.height * .78)} L ${round(node.width * .4)} ${round(node.height * .48)} L ${round(node.width * .56)} ${round(node.height * .65)} L ${round(node.width * .72)} ${round(node.height * .4)} L ${round(node.width * .9)} ${round(node.height * .78)} Z" fill="#98a2b3"/>`;
      break;
    case 'WIDGET':
      shape = widgetPlaceholder(node);
      break;
    case 'GROUP':
      break;
    default:
      shape = `<rect width="${round(node.width)}" height="${round(node.height)}" rx="${radius}" fill="${fill}"${strokeAttrs}/>`;
  }

  const children = (node.children ?? []).map((child) => renderNode(child)).join('');
  return `<g transform="${transform}" opacity="${Math.max(0, Math.min(1, node.opacity))}">${shape}${children}</g>`;
}

function widgetPlaceholder(node: DesignNode): string {
  const width = Math.max(1, node.width);
  const height = Math.max(1, node.height);
  const type = escapeXml(node.widget?.layoutType || 'Chart');
  const bars = [0.3, 0.62, 0.44, 0.78].map((ratio, index) => {
    const barWidth = width * .11;
    const x = width * (.2 + index * .16);
    const barHeight = height * ratio * .55;
    return `<rect x="${round(x)}" y="${round(height * .78 - barHeight)}" width="${round(barWidth)}" height="${round(barHeight)}" rx="2" fill="#6172f3"/>`;
  }).join('');
  return `<rect width="${round(width)}" height="${round(height)}" rx="8" fill="#ffffff" stroke="#d0d5dd" stroke-width="1"/>
    <text x="${round(width * .08)}" y="${round(height * .18)}" fill="#475467" font-family="Arial,sans-serif" font-size="${Math.max(8, Math.min(18, height * .1))}" font-weight="600">${type.slice(0, 28)}</text>${bars}`;
}

function fallbackThumbnail(name: string): string {
  return svgUrl(`<rect width="240" height="135" fill="#f2f4f7"/><rect x="54" y="31" width="132" height="73" rx="8" fill="#ffffff" stroke="#d0d5dd"/><path d="M78 82l22-23 18 17 20-27 24 33z" fill="#98a2b3"/><text x="120" y="121" text-anchor="middle" fill="#667085" font-family="Arial,sans-serif" font-size="11">${escapeXml(name).slice(0, 28)}</text>`);
}

function svgUrl(body: string): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${PREVIEW_WIDTH}" height="${PREVIEW_HEIGHT}" viewBox="0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}">${body}</svg>`)}`;
}

function safeColor(value: unknown, fallback: string): string {
  const color = String(value ?? '').trim();
  return /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\)|[a-z]+)$/i.test(color) ? color : fallback;
}

function escapeXml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[char]!);
}

function round(value: number): number { return Math.round(value * 100) / 100; }

/** SVG stroke-opacity for a stroke's "%" (absent / 100 = none). */
function strokeOpacityAttr(opacity: number | undefined): string {
  const value = Number(opacity);
  return Number.isFinite(value) && value < 100 ? ` stroke-opacity="${Math.max(0, value) / 100}"` : '';
}
